import React, { useEffect, useState } from "react";
import { Box, Grid2, Typography } from "@mui/material";
import { ThemeProperties } from "../../Theme/ThemeProperties";
import { decryptTheParams, encryptTheParams } from "../../Helper/QueryParams/EncryptDecrypt";
import { useNavigate } from "react-router-dom";
import TabWithColorDigitalTwins from "../../Features/Tabs/TabWithColorDigitalTwins";
import ScreenContainer from "../../Components/ScreenContainer";
import StatsSection from "../../PagesContent/Overview/StatsSection";
import MapAndIssueStreamSection from "../../PagesContent/Overview/MapAndIssueStreamSection";
import GlobalFilter from "../../PagesContent/Overview/components/GlobalFilter";
import FilterBar from "../../Components/FilterBar";
import MultiSelectDropdown from "../../PagesContent/Overview/components/MultiSelectDropdown";
import AppCache from "../../Helper/Common/AppCache";
import { getOrgInfo } from "../../Api/Api";
import useGlobalFilterData from "../../Hooks/useGlobalFilterData";


const OverviewMain = () => {
    const navigate = useNavigate();
    const queryParams = decryptTheParams();
    const [tabValue, setTabValue] = useState(0);
    const [globalFilterConfig, setGlobalFilterConfig] = useState(
        () => AppCache.getItem("globalFilter")?.find((f) => f.type === "unifiedGlobalFilter") || null
    );

    const {
        allFleets,
        selectedFleets,
        allOnboardingLocations,
        allOnboardingLocationsHierarchy,
        globalFilters,
        setGlobalFilters,
        fetchFleets,
        fetchOnboardingLocations,
        handleFleetAndLocationChange,
        canUseGlobalAdvancedFilter,
        fleetsLoaded,
        filtersLoading,
    } = useGlobalFilterData();

    const tabs = [
        {
            id: 0,
            name: 'Home',
            color: ThemeProperties.purple,
        }
    ]

    const handleDepotSelection = (selectedOptions) => {
        setGlobalFilters(prev => ({
            ...prev,
            onboardingLocations: { ...prev.onboardingLocations, depots: selectedOptions },
        }));
    };

    const changeTab = (newValue) => {
        setTabValue(newValue);
        encryptTheParams({ ...queryParams, tabValue: newValue }, navigate);
    };

    useEffect(() => {
        if (!globalFilterConfig) {
            getOrgInfo().then((res) => {
                if (res?.responseStatus?.code === 200) {
                    const globalFilter = res.response.metaInfo?.globalFilter || [];
                    AppCache.setItem("globalFilter", globalFilter);
                    const config = globalFilter.find((f) => f.type === "unifiedGlobalFilter") || null;
                    setGlobalFilterConfig(config);
                }
            });
        }
    }, []);

    useEffect(() => {
        fetchFleets();
    }, [tabValue]);

    useEffect(() => {
        if (!fleetsLoaded) return;
        const defaultOperationalStatus = ["operational", "non_operational"];
        if (allFleets.length === 0) {
            // Org has no fleets — still initialise globalFilters so downstream API calls fire
            setGlobalFilters(prev => ({
                ...prev,
                operationalStatus: prev.operationalStatus || defaultOperationalStatus,
                allOperationalStatus: defaultOperationalStatus,
            }));
            return;
        }
        if (selectedFleets.length > 0) {
            fetchOnboardingLocations(selectedFleets, {
                operationalStatus: defaultOperationalStatus,
                allOperationalStatus: defaultOperationalStatus,
            });
        }
    }, [fleetsLoaded, allFleets, selectedFleets])

    return (
        <>
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0, borderBottom: "1px solid #E4E3E3" }}>
                <TabWithColorDigitalTwins ChangeTheTab={changeTab} tabs={tabs} />
            </Box>

            {/* page wrapper */}
            <Box sx={{ px: 3, py: 2, mb: 2 }}>

                <Grid2 container gap={2} flexDirection={'column'}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="fw400s14c494949" sx={{ fontSize: '18px', lineHeight: '24px' }}>
                            Here's What's Happening in Your Fleet Today
                        </Typography>
                        <FilterBar
                          testId="overview-filters"
                          filters={[
                            {
                              key: "customerLocation",
                              primary: true,
                              node: (
                                <>
                            {canUseGlobalAdvancedFilter && globalFilterConfig ? (
                                <GlobalFilter
                                    isLoading={filtersLoading}
                                    allFleets={allFleets}
                                    allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
                                    selectedFleetIds={globalFilters?.fleets || []}
                                    selectedLocations={globalFilters?.hierarchicalLocation}
                                    onChange={handleFleetAndLocationChange}
                                />
                            ) : (
                                allOnboardingLocations?.depots?.length > 0 && (
                                    <MultiSelectDropdown
                                        selectedOptions={globalFilters?.onboardingLocations?.depots || []}
                                        options={allOnboardingLocations?.depots || []}
                                        label="Depot"
                                        onChange={handleDepotSelection}
                                    />
                                )
                            )}
                                </>
                              ),
                            },
                          ]}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <StatsSection globalFilters={globalFilters} />
                        <MapAndIssueStreamSection globalFilters={globalFilters} />
                    </Box>
                </Grid2>
            </Box>
        </>
    );
}

export default OverviewMain;
